import { mat4 } from 'gl-matrix';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { Image3D } from '../patient/image3d';
import { Patient } from '../patient/patient';
import { RigidRegistration } from '../patient/rigidRegistration';
import {
    ImageRegistrationsViewModel,
    RegistrationStoreState,
    RegistrationViewModel,
} from '../readmodels/RegistrationReadModel';
import { DicomProcessor } from '../services/dicomProcessor';

const EMPTY_SNAPSHOT: RegistrationStoreState = Object.freeze({
    primaryImageSeriesUid: undefined,
    imageRegistrations: Object.freeze([]),
    selectedSopInstanceUid: undefined,
    selectedImageSeriesUid: undefined,
    selectedRegistration: undefined,
});

export class RegistrationStore {
    private listeners = new Set<() => void>();
    private version = 0;
    private cachedSnapshot: RegistrationStoreState = EMPTY_SNAPSHOT;
    private cachedVersion = -1;
    private patient: Patient | undefined;
    private primaryImage: Image3D | undefined;
    private selectedSopUid: string | undefined;
    private selectedImageSeriesUid: string | undefined;

    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    getSnapshot = (): RegistrationStoreState => {
        if (this.cachedVersion === this.version) {
            return this.cachedSnapshot;
        }
        this.cachedSnapshot = this.createSnapshot();
        this.cachedVersion = this.version;
        return this.cachedSnapshot;
    };

    /**
     * Sets the current patient and primary image context.
     * Call this when the user changes patient or primary image.
     */
    setContext(
        patient: Patient | undefined,
        primaryImage: Image3D | undefined
    ): void {
        if (this.patient === patient && this.primaryImage === primaryImage) {
            return;
        }
        this.patient = patient;
        this.primaryImage = primaryImage;
        this.selectedSopUid = undefined;
        this.selectedImageSeriesUid = undefined;
        this.notify();
    }

    /**
     * Removes a registration by SOP Instance UID.
     * Ignores approved registrations.
     */
    deleteRegistration(sopInstanceUid: string): void {
        const reg = this.getRegistrationBySopUid(sopInstanceUid);
        if (!reg) {
            return;
        }

        if (reg.isApproved) {
            console.warn(
                `Cannot delete approved registration ${sopInstanceUid}`
            );
            return;
        }

        this.patient!.removeRegistration(reg);
        if (this.selectedSopUid === sopInstanceUid) {
            this.selectedSopUid = undefined;
            this.selectedImageSeriesUid = undefined;
        }
        this.notify();
    }

    /**
     * Renames a registration by SOP Instance UID.
     * Ignores approved registrations.
     */
    renameRegistration(sopInstanceUid: string, newLabel: string): void {
        const reg = this.getRegistrationBySopUid(sopInstanceUid);
        if (!reg) {
            return;
        }

        if (reg.isApproved) {
            console.warn(
                `Cannot rename approved registration ${sopInstanceUid}`
            );
            return;
        }

        reg.contentLabel = newLabel;
        this.notify();
    }

    /**
     * Sets the approval status of a registration by SOP Instance UID.
     */
    setRegistrationApprovalStatus(
        sopInstanceUid: string,
        status: DICOMApprovalStatus
    ): void {
        const reg = this.getRegistrationBySopUid(sopInstanceUid);
        if (!reg) {
            return;
        }

        reg.setApprovalStatus(status);
        this.notify();
    }

    /**
     * Creates a new registration with the given initial transform
     * Returns the domain object so callers can immediately use it
     * for viewport activation.
     */
    createRegistration(
        primaryImage: Image3D,
        secondaryImage: Image3D,
        initialTransform: mat4 = mat4.create(),
        contentCreatorName: string = ''
    ): RigidRegistration | undefined {
        if (!this.patient) {
            return undefined;
        }

        if (
            primaryImage.frameOfReferenceUid ===
            secondaryImage.frameOfReferenceUid
        ) {
            return undefined;
        }

        const label = this.getNextRegistrationLabel();

        const dicomParent = DicomProcessor.createEmptyDicomRegistration(
            this.patient,
            primaryImage,
            secondaryImage,
            label,
            initialTransform,
            contentCreatorName
        );

        const reg = new RigidRegistration(
            primaryImage.frameOfReferenceUid,
            secondaryImage.frameOfReferenceUid,
            dicomParent.transformation,
            dicomParent
        );

        this.patient.addRegistration(reg);
        this.notify();
        return reg;
    }

    /**
     * Sets the currently selected registration by SOP Instance UID.
     * Pass undefined to clear selection.
     */
    selectRegistration(
        sopInstanceUid: string | undefined,
        imageSeriesUid?: string
    ): void {
        if (
            this.selectedSopUid === sopInstanceUid &&
            this.selectedImageSeriesUid === imageSeriesUid
        ) {
            return;
        }
        this.selectedSopUid = sopInstanceUid;
        this.selectedImageSeriesUid = imageSeriesUid;
        this.notify();
    }

    /**
     * Returns the live domain object for the currently selected
     * registration. Use this only when the mutable domain object
     * is required (e.g. viewport/engine operations).
     */
    getSelectedRegistration(): RigidRegistration | undefined {
        if (!this.selectedSopUid) {
            return undefined;
        }
        return this.getRegistrationBySopUid(this.selectedSopUid);
    }

    getRegistrationBySopUid(
        sopInstanceUid: string
    ): RigidRegistration | undefined {
        return this.patient?.getRegistrationWithSopInstanceUid(sopInstanceUid);
    }

    getImageBySeriesUid(seriesUid: string): Image3D | undefined {
        return this.patient?.images.find((i) => i.seriesUid === seriesUid);
    }

    getImageByForUid(frameOfReferenceUid: string): Image3D | undefined {
        return this.patient?.images.find(
            (i) => i.frameOfReferenceUid === frameOfReferenceUid
        );
    }

    /**
     * Creates a new registration that is the inverse of the given
     * registration. The new registration swaps source and target
     * frames of reference and inverts the transformation matrix.
     *
     * Returns the new registration and the resolved images
     * (newPrimaryImage = old secondary, newSecondaryImage = old primary),
     * or undefined if the operation cannot be performed.
     */
    invertRegistration(
        sopInstanceUid: string,
        contentCreatorName: string = ''
    ):
        | {
              registration: RigidRegistration;
              newPrimaryImage: Image3D;
              newSecondaryImage: Image3D;
          }
        | undefined {
        const existingReg = this.getRegistrationBySopUid(sopInstanceUid);
        if (!existingReg) {
            console.warn(
                `Cannot invert: registration ${sopInstanceUid} not found`
            );
            return undefined;
        }

        if (existingReg.isApproved) {
            console.warn(
                `Cannot invert approved registration ${sopInstanceUid}`
            );
            return undefined;
        }

        const oldPrimaryImage = this.getImageByForUid(
            existingReg.sourceFrameOfReferenceUid
        );
        const oldSecondaryImage = this.getImageByForUid(
            existingReg.targetFrameOfReferenceUid
        );

        if (!oldPrimaryImage || !oldSecondaryImage) {
            console.warn(
                'Cannot invert: could not resolve images from FOR UIDs'
            );
            return undefined;
        }

        const invertedTransform = mat4.invert(
            mat4.create(),
            existingReg.transformation
        );
        if (!invertedTransform) {
            console.warn(
                'Cannot invert: transformation matrix is not invertible'
            );
            return undefined;
        }

        // New registration: old secondary becomes source, old primary becomes target
        const newReg = this.createRegistration(
            oldSecondaryImage,
            oldPrimaryImage,
            invertedTransform,
            contentCreatorName
        );

        if (!newReg) {
            return undefined;
        }

        newReg.contentLabel = this.getNextInvertedLabel();
        newReg.contentDescription = `This registration is inverted from ${existingReg.contentLabel}`;

        return {
            registration: newReg,
            newPrimaryImage: oldSecondaryImage,
            newSecondaryImage: oldPrimaryImage,
        };
    }

    private getNextRegistrationLabel(): string {
        return this.getNextLabelWithPrefix('REG');
    }

    private getNextInvertedLabel(): string {
        return this.getNextLabelWithPrefix('INV_REG');
    }

    private getNextLabelWithPrefix(prefix: string): string {
        const snapshot = this.getSnapshot();
        let max = 0;
        for (const imgReg of snapshot.imageRegistrations) {
            for (const reg of imgReg.registrations) {
                const label = reg.contentLabel ?? '';
                if (!label.startsWith(prefix)) {
                    continue;
                }
                const num = Number(label.slice(prefix.length));
                if (Number.isFinite(num) && num > max) {
                    max = num;
                }
            }
        }
        return `${prefix}${max + 1}`;
    }

    private notify(): void {
        this.version++;
        this.listeners.forEach((l) => l());
    }

    private createSnapshot(): RegistrationStoreState {
        if (!this.patient || !this.primaryImage) {
            return EMPTY_SNAPSHOT;
        }

        const allRegs = this.patient.getAllRegistrationsForPrimary(
            this.primaryImage
        );

        const imageRegistrations: ImageRegistrationsViewModel[] = allRegs.map(
            (imgReg) =>
                Object.freeze({
                    imageSeriesUid: imgReg.image.seriesUid,
                    registrations: Object.freeze(
                        (imgReg.registrations ?? []).map(
                            (reg): RegistrationViewModel =>
                                Object.freeze({
                                    sopInstanceUid: reg.sopInstanceUid,
                                    contentLabel: reg.contentLabel,
                                    contentDescription: reg.contentDescription,
                                    contentCreatorName: reg.contentCreatorName,
                                    contentDate: reg.contentDate,
                                    sourceFrameOfReferenceUid:
                                        reg.sourceFrameOfReferenceUid,
                                    targetFrameOfReferenceUid:
                                        reg.targetFrameOfReferenceUid,
                                    approvalStatus: reg.approvalStatus,
                                    isApproved: reg.isApproved,
                                    isNew: reg.isNew,
                                })
                        )
                    ),
                })
        );

        let selectedRegistration: RegistrationViewModel | undefined;
        if (this.selectedSopUid) {
            for (const imgReg of imageRegistrations) {
                selectedRegistration = imgReg.registrations.find(
                    (r) => r.sopInstanceUid === this.selectedSopUid
                );
                if (selectedRegistration) {
                    break;
                }
            }
        }

        return Object.freeze({
            primaryImageSeriesUid: this.primaryImage.seriesUid,
            imageRegistrations: Object.freeze(imageRegistrations),
            selectedSopInstanceUid: this.selectedSopUid,
            selectedImageSeriesUid: this.selectedImageSeriesUid,
            selectedRegistration,
        });
    }
}
